// packages/console/src/api/deliveries.ts
//
// FT-3 orchestrator client: delivery targets (Phase 1 output destinations). The
// inline Output select on a staged row reads this list. The orchestrator proxies
// the tenancy active-destination list at GET /v1/delivery-targets, returning
// {targets:[{id,kind,name,config}]}. Delivery is gated OFF today, so that route
// answers 404 in a delivery-disabled build; listDeliveryTargets treats 404 as
// "no external destinations" and returns an empty list, so the Output select
// falls back to a single "Media library" option (value "" = local, no external
// push). When delivery is enabled later, the list fills with real destinations.

import { servicePaths } from "./config";
import { request } from "./http";

// DeliveryTarget is one selectable output destination: an opaque id (tenancy
// uuid), a kind (media_library | webhook), and a human name. config rides the
// wire shape but the picker never needs it, so it is intentionally dropped.
export interface DeliveryTarget {
  id: string;
  kind: string;
  name: string;
}

// The orchestrator returns the tenancy target list under "targets".
interface DeliveryTargetsResponse {
  targets: DeliveryTarget[];
}

const j = servicePaths.jobs;

// listDeliveryTargets returns the workspace's selectable output destinations, or
// an empty list when delivery is not enabled (the endpoint 404s). Any other
// error propagates so the caller can surface an honest failure (R10). A 404 is
// the expected steady state today, so it is NOT an error: it means "no external
// destinations, use the local media library".
export async function listDeliveryTargets(signal?: AbortSignal): Promise<DeliveryTarget[]> {
  try {
    const res = await request<DeliveryTargetsResponse>(`${j}/v1/delivery-targets`, { signal });
    return Array.isArray(res.targets) ? res.targets : [];
  } catch (err) {
    if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
      return [];
    }
    throw err;
  }
}
